import React, { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { useField } from 'formik'
import { useTranslation } from 'react-i18next'
import { ActionMeta, InputActionMeta, Props as SelectProps, SingleValue } from 'react-select'
import { SpaceProps, WidthProps } from 'styled-system'

import { Select } from '@/atoms/input'
import { isEmpty, isNil } from '@/helpers/type-guards'
import useDebounce from '@/hooks/use-debounce'
import { SelectOption } from '@/models/common'

import FieldWrapper from './field-wrapper'

interface OwnProps {
  label?: ReactNode
  name?: string
  tooltip?: string
  fieldProps?: SpaceProps & WidthProps
  selectedOptionSearchValue?: string
  onSelectChange?: (selectedOption: SelectOption) => void
  searchOnMenuOpen?: (selectedOption: SelectOption) => void
  hideErrorMessage?: boolean
  loading?: boolean
  menuHeight?: number
  searchOnInputChange?: (text: string | number | boolean | object | (string | object)[]) => void
}

export type AsyncSelectProps = OwnProps & SelectProps<SelectOption>

const AsyncSelectField: FunctionComponent<AsyncSelectProps> = ({
  label,
  name,
  tooltip,
  options: initialOptions,
  fieldProps,
  searchOnInputChange,
  selectedOptionSearchValue,
  onSelectChange,
  components,
  noOptionsMessage,
  loading,
  searchOnMenuOpen,
  hideErrorMessage,
  placeholder,
  menuHeight,
  ...rest
}) => {
  const { t } = useTranslation()
  const selectReference = useRef(null)
  const [field, meta, helpers] = useField(name)
  const hasError: boolean = meta.touched && !!meta.error
  const [options, setOptions] = useState(initialOptions)
  const selectedOption = options?.find((option: SelectOption) => option.value === field.value) || null
  const [searchTerm, setSearchTerm] = useState(null)
  const debouncedSearchTerm = useDebounce(searchTerm, 1000)
  const cleanSelectField = useCallback((): void => {
    setOptions([])
    helpers.setValue && helpers.setValue('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    !isNil(debouncedSearchTerm) && searchOnInputChange(debouncedSearchTerm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  useEffect(() => {
    initialOptions && setOptions(initialOptions)
  }, [initialOptions])

  useEffect(() => {
    isEmpty(field.value) && cleanSelectField()
  }, [field.value, cleanSelectField])

  const handleOnChange = (option: SelectOption, action: ActionMeta<SelectOption>): void => {
    if (action.action === 'clear') {
      onSelectChange && onSelectChange(null)
      cleanSelectField()

      return
    }
    onSelectChange && onSelectChange(option)
    helpers?.setValue(option.value)

    return
  }
  const handleInputChange = (newValue: string, actionMeta: InputActionMeta): void => {
    if (actionMeta.action === 'input-change') {
      if (newValue.length === 0) {
        onSelectChange && onSelectChange(null)
        setSearchTerm('')
        cleanSelectField()
      }
      return setSearchTerm(newValue)
    }

    return
  }
  const searchBySelectedOption = (): void => {
    if (selectedOption) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      selectReference.current.state.inputValue = selectedOptionSearchValue
        ? selectedOption[selectedOptionSearchValue]
        : (selectedOption as SelectOption).value
      searchOnMenuOpen && searchOnMenuOpen(selectedOption as SelectOption)
      return
    }
    searchOnMenuOpen && searchOnMenuOpen(selectedOption as SelectOption)
  }
  const defaultNoOptionsMessage = (): string => t('form.no_matching_results')

  return (
    <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
      <Select
        ref={selectReference}
        options={options}
        hasError={hasError}
        onChange={handleOnChange}
        value={selectedOption as SingleValue<SelectOption>}
        name={field.name}
        onInputChange={handleInputChange}
        onMenuOpen={searchBySelectedOption}
        components={components}
        noOptionsMessage={noOptionsMessage || defaultNoOptionsMessage}
        placeholder={placeholder || t('global.placeholder.search')}
        menuHeight={menuHeight}
        {...rest}
      />
    </FieldWrapper>
  )
}

AsyncSelectField.displayName = 'AsyncSelectField'

export default AsyncSelectField
